/** Apply add/remove class sets to an element. Each set is a string of space-separated classes. */
function applyButtonClasses(el, { add = [], remove = [] }) {
  add.flatMap((s) => s.split(/\s+/).filter(Boolean)).forEach((c) => el.classList.add(c));
  remove.flatMap((s) => s.split(/\s+/).filter(Boolean)).forEach((c) => el.classList.remove(c));
}

/** Variant button UI class sets: single source of truth for selected/deselected styles */
const VARIANT_BUTTON_CLASSES = {
  pill: {
    selected: {
      add: ["!font-medium", "!bg-lightgray", "border-secondaryhighlight"],
      remove: ["border-separatorgray", "!border-lightgray"],
    },
    deselected: {
      add: ["bg-white", "border-separatorgray"],
      remove: ["!font-medium", "!bg-lightgray", "border-secondaryhighlight"],
    },
  },
  swatch: {
    selected: { add: ["ring-[1px]", "ring-secondaryhighlight"], remove: [] },
    deselected: { add: [], remove: ["ring-[1px]", "ring-secondaryhighlight", "bg-gray-100"] },
  },
  variantImage: {
    selected: { add: ["ring-secondaryhighlight", "ring-[1px]"], remove: ["ring-transparent"] },
    deselected: { add: ["border-transparent"], remove: ["ring-secondaryhighlight", "ring-[1px]"] },
  },
};

const isDefaultTitleOption = (value) => value === "Default Title";

/** Combined listings may use "Default Title" on one axis; treat it as a wildcard when matching. */
const variantOptionMatches = (variantOption, selectedOption) => {
  if (isDefaultTitleOption(variantOption)) return true;
  return variantOption === selectedOption;
};

const selectionMatchesVariant = (selectedOptions, variantOptions) => selectedOptions.every((value, index) => variantOptionMatches(variantOptions[index], value));

const countMatchingOptionsExcept = (variantOptions, selectedOptions, excludeIndex) =>
  selectedOptions.reduce((sum, value, index) => {
    if (index === excludeIndex) return sum;
    return sum + (variantOptionMatches(variantOptions[index], value) ? 1 : 0);
  }, 0);

const pickBestVariant = (candidates, selectedOptions, excludeIndex) =>
  candidates.reduce(
    (best, variant) => {
      const score = countMatchingOptionsExcept(variant.options, selectedOptions, excludeIndex);
      return score > best.score ? { variant, score } : best;
    },
    { variant: null, score: -1 },
  ).variant;

/** Omit Shopify's default single-variant label from user-facing option strings. */
const formatChangedOptionValues = (values, fallback = "") => values.filter((v) => v !== null && v !== "" && !isDefaultTitleOption(v)).join(", ") || fallback;

class VariantSelects extends HTMLElement {
  constructor() {
    super();

    // Cache DOM elements and state
    this.cache = {
      selects: null,
      customButtons: null,
      productForms: null,
      priceElement: null,
    };

    this.state = {
      lastVariantId: null,
      pendingFetch: null,
      debounceTimer: null,
      suppressVariantChange: false,
    };

    this.selectListenersInitialized = false;

    this.addEventListener("change", this.onVariantChange);
    this.boundOnPageLoad = this.onPageLoad.bind(this);
    document.addEventListener("DOMContentLoaded", this.boundOnPageLoad);
  }

  onPageLoad() {
    this.cacheElements();
    this.setup();
    this.setupCustomVariantButtons();
    this.updateCustomVariantButtonUI();
    this.triggerValidCombinationFallback();
    document.removeEventListener("DOMContentLoaded", this.boundOnPageLoad);
  }

  cacheElements() {
    this.cache.selects = this.querySelectorAll("select");
    this.cache.customButtons = this.querySelectorAll("[data-select-id][data-value]");
    this.cache.productForms = document.querySelectorAll(`#product-form-${this.dataset.section}-fields, #product-form-installment-${this.dataset.section}`);
    this.cache.priceElement = document.getElementById(`price-${this.dataset.section}`);
  }

  setupCustomVariantButtons() {
    // Remove existing listeners to prevent duplicates
    this.cache.customButtons.forEach((btn) => {
      if (btn._variantBtnHandler) {
        btn.removeEventListener("click", btn._variantBtnHandler);
      }
    });

    // Add new listeners - convert NodeList to Array to ensure we have fresh references
    const buttonsArray = Array.from(this.cache.customButtons);

    buttonsArray.forEach((btn) => {
      // Verify button is actually in the DOM before attaching listener
      if (!document.contains(btn)) {
        return;
      }

      btn._variantBtnHandler = () => {
        const select = document.getElementById(btn.dataset.selectId);
        if (select) {
          select.value = btn.dataset.value;
          select.dispatchEvent(new Event("change", { bubbles: true }));
        }
      };
      btn.addEventListener("click", btn._variantBtnHandler);
    });

    // Only add select change listeners ONCE to prevent accumulation
    this.cache.selects.forEach((select) => {
      if (select.getAttribute("data-listener-initialized") === "false") {
        select.addEventListener("change", (e) => {
          this.updateCustomVariantButtonUI(e);
        });
        select.setAttribute("data-listener-initialized", "true");
      }
    });
  }
  updateCustomVariantButtonUI(changeEvent) {
    if (!this.isConnectedProductChange(changeEvent) && this.triggerValidCombinationFallback(changeEvent)) return;
    this.cache.selects.forEach((select) => {
      const value = select.value;
      const btns = this.querySelectorAll(`[data-select-id="${select.id}"]`);

      btns.forEach((btn) => {
        const swatchElement = btn.querySelector(".rounded-full");
        const isSwatch = !!swatchElement;
        const isVariantImage = btn.hasAttribute("data-variant-image");
        const isSelected = btn.dataset.value === value;

        if (isSelected) {
          btn.setAttribute("aria-current", "true");
        } else {
          btn.removeAttribute("aria-current");
        }

        const state = isSelected ? "selected" : "deselected";
        if (isSwatch) {
          applyButtonClasses(btn, VARIANT_BUTTON_CLASSES.swatch[state]);
        } else if (isVariantImage) {
          applyButtonClasses(btn, VARIANT_BUTTON_CLASSES.variantImage[state]);
        } else {
          applyButtonClasses(btn, VARIANT_BUTTON_CLASSES.pill[state]);
        }
      });
    });
  }

  setup() {
    this.variantData = this.getVariantData();
    this.updateOptions();
    this.updateMasterId();
  }

  onVariantChange(event) {
    if (this.state.suppressVariantChange) return;

    // Clear any pending debounced calls
    if (this.state.debounceTimer) {
      clearTimeout(this.state.debounceTimer);
    }

    this.toggleAddButton(true, "", false);
    this.updateOptions();
    if (this.isConnectedProductChange(event)) {
      void this.handleConnectedProductChange(event);
      return;
    }

    this.setup();
    if (this.triggerValidCombinationFallback(event)) return;

    if (!this.currentVariant) {
      this.toggleAddButton(true, "", true);
      this.setUnavailable();
      document.dispatchEvent(
        new CustomEvent("productVariantUpdated", {
          detail: { sku: null },
        }),
      );
    } else {
      this.updateURL();
      this.updateVariantInput();

      // Check availability: disable Add to Cart for sold-out variants
      if (!this.currentVariant.available) {
        this.toggleAddButton(true, window.variantStrings?.soldOut || "Ausverkauft", true);
      } else {
        this.toggleAddButton(false);
      }

      const changedIndex = event?.target ? Array.from(this.cache.selects).findIndex((select) => select === event.target) : -1;
      const fetchDelay = changedIndex === 0 ? 0 : 100;

      if (fetchDelay === 0) {
        this.renderProductInfo();
      } else {
        this.state.debounceTimer = setTimeout(() => {
          this.renderProductInfo();
        }, fetchDelay);
      }
    }
  }

  updateOptions() {
    this.options = Array.from(this.cache.selects, (select) => select.value);
  }

  isConnectedProductChange(event) {
    const select = event?.target;
    if (!(select instanceof HTMLSelectElement)) return false;

    const productUrl = select.options[select.selectedIndex]?.dataset.productUrl;
    if (!productUrl) return false;

    const currentProductUrl = new URL(this.dataset.url, window.location.origin);
    const targetUrl = new URL(productUrl, window.location.origin);
    return targetUrl.pathname !== currentProductUrl.pathname;
  }

  async handleConnectedProductChange(event) {
    const select = event?.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const selectedOption = select.options[select.selectedIndex];
    const productUrl = selectedOption?.dataset.productUrl;
    if (!productUrl) return;

    await this.changeConnectedProduct(productUrl, this.getOptionName(select), select.value);
  }

  async changeConnectedProduct(productUrl, clickedOptionName, clickedOptionValue) {
    const targetUrl = new URL(productUrl, window.location.origin);
    const currentSelects = Array.from(this.cache.selects);
    const selectedValuesByName = new Map(currentSelects.map((currentSelect) => [this.getOptionName(currentSelect), currentSelect.value]));

    if (this.state.pendingFetch) {
      this.state.pendingFetch.abort?.();
    }
    const controller = new AbortController();
    this.state.pendingFetch = controller;

    try {
      const productJsonUrl = new URL(`${targetUrl.pathname}.js`, targetUrl.origin);
      const response = await fetch(productJsonUrl.toString(), { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const targetProduct = await response.json();
      if (this.state.pendingFetch !== controller) return;
      this.state.pendingFetch = null;

      const targetVariants = targetProduct.variants || [];
      const targetOptionNames = (targetProduct.options || []).map((option) => (typeof option === "string" ? option : option.name));
      const requestedTargetOptions = targetOptionNames.map((optionName) => selectedValuesByName.get(optionName));
      const matchesRequestedOptions = (variant) => requestedTargetOptions.every((value, index) => value === undefined || variantOptionMatches(variant.options[index], value));

      let targetVariant = targetVariants.find(matchesRequestedOptions);
      let notification = null;

      if (!targetVariant) {
        targetVariant = targetVariants.reduce(
          (best, variant) => {
            const score = requestedTargetOptions.reduce((sum, value, index) => sum + (value !== undefined && variantOptionMatches(variant.options[index], value) ? 1 : 0), 0);
            return score > best.score ? { variant, score } : best;
          },
          { variant: null, score: -1 },
        ).variant;
        notification = this.createConnectedFallbackNotification(targetVariant, targetOptionNames, requestedTargetOptions, clickedOptionName, clickedOptionValue);
      }

      if (!targetVariant) throw new Error("Connected product has no matching variant");

      this.variantData = targetVariants;
      this.currentVariant = targetVariant;
      this.options = [...targetVariant.options];
      this.state.lastVariantId = null;

      for (const [index, optionName] of targetOptionNames.entries()) {
        const currentSelect = currentSelects.find((candidate) => this.getOptionName(candidate) === optionName);
        if (!currentSelect) continue;
        const value = targetVariant.options[index];
        if (this.selectHasOption(currentSelect, value)) currentSelect.value = value;
      }

      this.updateVariantInput();
      await this.renderProductInfo(targetUrl.pathname);
      this.variantData = null;
      this.setup();

      if (notification) {
        this.showSelectionChangeNotification(
          notification.optionName,
          notification.adjustedValueBefore,
          notification.adjustedValueAfter,
          notification.clickedOptionName,
          notification.clickedOptionValue,
        );
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Connected product change failed:", error);
      window.location.assign(targetUrl.toString());
    }
  }

  createConnectedFallbackNotification(bestVariant, targetOptionNames, requestedOptions, clickedOptionName, clickedOptionValue) {
    if (!bestVariant) return null;

    const adjustedIndexes = requestedOptions
      .map((value, index) => (value !== undefined && !variantOptionMatches(bestVariant.options[index], value) ? index : -1))
      .filter((index) => index >= 0);
    if (!adjustedIndexes.length) return null;

    return {
      optionName: targetOptionNames[adjustedIndexes[0]] || "",
      adjustedValueBefore: formatChangedOptionValues(requestedOptions.map((value, index) => (adjustedIndexes.includes(index) ? value : null))),
      adjustedValueAfter: formatChangedOptionValues(
        bestVariant.options.map((value, index) => (adjustedIndexes.includes(index) ? value : null)),
        window.variantStrings?.fallbackVariantLabel || "dieser Variante",
      ),
      clickedOptionName,
      clickedOptionValue,
    };
  }

  getOptionName(select) {
    return select.name.match(/\[(.*)\]/)?.[1] || select.name;
  }

  selectHasOption(select, value) {
    return Array.from(select.options).some((option) => option.value === value);
  }

  /**
   * Apply a fallback variant via section re-render so visibility-filtered selects stay in sync.
   * @returns {boolean} True when a fallback was scheduled.
   */
  applyFallbackVariant(bestVariant, notification) {
    this.state.suppressVariantChange = true;
    this.currentVariant = bestVariant;
    this.options = [...bestVariant.options];
    this.updateVariantInput();
    this.state.lastVariantId = null;

    const selects = Array.from(this.cache.selects);
    for (let i = 0; i < selects.length; i++) {
      const value = bestVariant.options[i];
      if (this.selectHasOption(selects[i], value)) {
        selects[i].value = value;
      }
    }
    this.updateOptions();

    this.renderProductInfo()
      .then(() => {
        this.cacheElements();
        this.setupCustomVariantButtons();
        this.updateCustomVariantButtonUI();
        if (notification) {
          this.showSelectionChangeNotification(
            notification.optionName,
            notification.adjustedValueBefore,
            notification.adjustedValueAfter,
            notification.clickedOptionName,
            notification.clickedOptionValue,
          );
        }
        if (!this.currentVariant?.available) {
          this.toggleAddButton(true, window.variantStrings?.soldOut || "Ausverkauft", true);
        } else {
          this.toggleAddButton(false);
        }
        // renderProductInfo already dispatched productVariantUpdated for this variant
        this.updateURL({ dispatch: false });
      })
      .catch((err) => {
        console.error("Fallback variant render failed:", err);
      })
      .finally(() => {
        this.state.suppressVariantChange = false;
      });

    return true;
  }

  /**
   * Trigger the valid combination fallback logic.
   * @param {Event} changeEvent - The change event object.
   * @returns {boolean} True when fallback handling took over the change event.
   */
  triggerValidCombinationFallback(changeEvent) {
    if (!this.variantData || !this.variantData.length) return false;
    if (!this.cache || !this.cache.selects) return false;

    // Ensure options array is current
    this.updateOptions();
    try {
      if (changeEvent) {
        const userSelectedValue = changeEvent.currentTarget.value;
        const previousSelectedOptions = this.options;
        const validCombinations = this.variantData;

        if (userSelectedValue) {
          // Determine which option position the user changed
          const changedOptionIndex = Array.from(this.cache.selects).findIndex((s) => s === changeEvent.currentTarget);

          const userRequestedCombination = previousSelectedOptions.map((val, i) => (i === changedOptionIndex ? userSelectedValue : val));
          const userRequestedValidCombination = validCombinations.some((variant) => selectionMatchesVariant(userRequestedCombination, variant.options));
          if (userRequestedValidCombination) return false;

          let candidates = validCombinations.filter((variant) => variant.options[changedOptionIndex] === userSelectedValue);
          let bestVariant = pickBestVariant(candidates, previousSelectedOptions, changedOptionIndex);

          // e.g. Jersey + unavailable color → revert to Frottee while keeping the color
          if (!bestVariant) {
            bestVariant = pickBestVariant(validCombinations, previousSelectedOptions, changedOptionIndex);
          }

          if (bestVariant) {
            const hasChanges = previousSelectedOptions.some((val, i) => !variantOptionMatches(bestVariant.options[i], val));
            if (!hasChanges) return false;

            const clickedOptionName = this.cache.selects[changedOptionIndex].name.match(/\[(.*)\]/)[1];
            const clickedOptionValue = userSelectedValue;
            const adjustedIndex = previousSelectedOptions.findIndex((val, i) => !variantOptionMatches(bestVariant.options[i], val));

            const adjustedSelectName = this.cache.selects[adjustedIndex]?.name || "";
            const optionName = adjustedSelectName.match(/\[(.*)\]/)?.[1] || adjustedSelectName;
            const adjustedValueBefore = formatChangedOptionValues(previousSelectedOptions.map((val, i) => (!variantOptionMatches(bestVariant.options[i], val) ? val : null)));
            const adjustedValueAfter = formatChangedOptionValues(
              bestVariant.options.map((val, i) => (!variantOptionMatches(previousSelectedOptions[i], val) ? val : null)),
              window.variantStrings?.fallbackVariantLabel || "dieser Variante",
            );

            return this.applyFallbackVariant(bestVariant, {
              optionName,
              adjustedValueBefore,
              adjustedValueAfter,
              clickedOptionName,
              clickedOptionValue,
            });
          }
        }
      }
    } catch (err) {
      console.error("Error triggering valid combination fallback:", err);
    }
    return false;
  }

  /**
   * Show a subtle notification when variant selection is automatically changed.
   * Pass an optional `body` (HTML string) to override the default "Deine Wunschkombi…" message.
   */
  showSelectionChangeNotification(optionName, oldValue, newValue, clickedOptionName, clickedOptionValue, body = null) {
    const attrMode = this.getAttribute("data-notice-mode") || this.getAttribute("mode") || this.dataset.noticeMode;
    const rawMode = (attrMode || "il").toLowerCase().trim();
    let mode;
    // Map shorthand codes and keep backward compatibility
    if (rawMode === "tt") {
      mode = "toast-top";
    } else if (rawMode === "tb") {
      mode = "toast-bottom";
    } else if (rawMode === "il") {
      mode = "inline";
    } else if (rawMode === "toast-top" || rawMode === "toast_top" || rawMode === "top") {
      mode = "toast-top";
    } else if (rawMode === "toast-bottom" || rawMode === "toast" || rawMode === "bottom") {
      mode = "toast-bottom";
    } else if (rawMode === "inline") {
      mode = "inline";
    } else {
      mode = "inline";
    }

    // Ensure basic keyframes exist once
    if (!document.querySelector("#variant-notification-styles")) {
      const style = document.createElement("style");
      style.id = "variant-notification-styles";
      style.textContent = `
        @keyframes slideInUp { from { opacity: 0; transform: translateX(-50%) translateY(16px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
        @keyframes slideInDown { from { opacity: 0; translateY(-16px); } to { opacity: 1; translateY(0); } }
        @keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }
      `;
      document.head.appendChild(style);
    }

    const toastMsg =
      body ??
      `${optionName} <span class="font-medium">${oldValue}</span> ist für ${clickedOptionName} ${clickedOptionValue} nicht verfügbar. Wie wär's mit <span class="font-medium">${newValue}</span>?`;

    const isToastMode = mode === "toast" || mode === "toast-bottom" || mode === "toast_top" || mode === "toastbottom" || mode === "toast-top" || mode === "toasttop";
    if (isToastMode) {
      // Remove any existing toast
      const existingToast = document.querySelector(".variant-change-notification");
      const toast = existingToast ? existingToast : document.createElement("div");
      toast.className = "variant-change-notification";

      toast.innerHTML = `
        <div class="px-3 py-2 bg-beige items-center text-[#384d3f] leading-6 rounded-md flex lg:inline-flex shadow-lg" role="status" aria-live="polite">
          <span class="inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-bold mr-2" style="background:#9CD8B3;color:#000;letter-spacing:.02em;">Info</span>
          <span class="mr-2 text-left flex-auto text-xs">${toastMsg}</span>
        </div>
      `;
      const isTop = mode === "toast-top" || mode === "toast_top";
      const verticalRule = isTop ? "top: calc(env(safe-area-inset-top, 0px) + 24px)" : "bottom: calc(env(safe-area-inset-bottom, 0px) + 24px)";
      toast.style.cssText = [
        "position: fixed",
        verticalRule,
        "left: 0",
        "right: 0",
        "margin-left: auto",
        "margin-right: auto",
        "z-index: 2147483647",
        "width: min(90vw, 800px)",
        // Only add animation if it's a new toast. If the toast already exists, remove any previous animation style.
        ...(existingToast ? ["animation: none"] : [`animation: ${isTop ? "slideInDown" : "slideInUp"} 0.25s ease-out`]),
      ].join(";");
      if (!existingToast) {
        this.prepend(toast);
      }

      // Collapse and remove after delay
      if (this._toastTimeout) clearTimeout(this._toastTimeout);

      this._toastTimeout = setTimeout(() => {
        toast.style.animation = "fadeOut 0.25s ease-out";
        setTimeout(() => toast.remove(), 250);
      }, 8000);
      return;
    }

    // Inline mode: compact banner with height animation (shifts content down)
    const existingToast = document.querySelector(".variant-change-notification");
    const toast = existingToast ? existingToast : document.createElement("div");
    toast.className = "variant-change-notification mb-3";

    let elementToDismiss;
    if (existingToast) {
      document.querySelector(".toast-message").innerHTML = toastMsg;
      elementToDismiss = toast;
    } else {
      // Also remove one in parent (we insert there so it survives variant-selector innerHTML refresh)
      const prevSibling = this.previousElementSibling;
      if (prevSibling?.classList?.contains("variant-change-notification")) prevSibling.remove();

      const wrapper = document.createElement("div");
      wrapper.className = "variant-change-notification mb-3";
      wrapper.style.cssText = "height:0; overflow:hidden; transition:height 250ms ease;";

      const inner = document.createElement("div");
      inner.setAttribute("role", "status");
      inner.setAttribute("aria-live", "polite");
      inner.className = "flex items-center lg:inline-flex rounded-md bg-beige";
      inner.style.cssText = `padding:4px 8px; line-height:1.3; color:#384d3f;`;

      const pill = document.createElement("span");
      pill.textContent = "Info";
      pill.className = "inline-flex items-center justify-center rounded-full uppercase mr-4";
      pill.style.cssText = "padding:2px 8px; font-size:10px; letter-spacing:.02em; background:#9CD8B3; color:#000;";

      const text = document.createElement("span");
      text.className = "toast-message text-left flex-auto text-sm";
      text.innerHTML = toastMsg;
      inner.appendChild(pill);
      inner.appendChild(text);
      wrapper.appendChild(inner);

      this.prepend(wrapper);
      elementToDismiss = wrapper;

      // Expand to content height
      requestAnimationFrame(() => {
        const target = inner.offsetHeight; // exact content height
        wrapper.style.height = target + "px";
      });
    }
    // Collapse and remove after delay
    if (this._toastTimeout) clearTimeout(this._toastTimeout);

    this._toastTimeout = setTimeout(() => {
      elementToDismiss.style.height = "0px";
      setTimeout(() => elementToDismiss.remove(), 250);
    }, 8000);
  }

  updateMasterId() {
    this.currentVariant = this.variantData.find((variant) => selectionMatchesVariant(this.options, variant.options));
  }

  /**
   * Sync the address bar with the current variant.
   * @param {{ dispatch?: boolean }} [options] - dispatch: false when productVariantUpdated was already sent for this variant
   */
  updateURL({ dispatch = true } = {}) {
    if (!this.currentVariant || this.dataset.updateUrl === "false") return;

    const productPath = this.dataset.url;
    const currentUrl = new URL(window.location.href);
    const nextUrl = new URL(productPath, window.location.origin);
    nextUrl.searchParams.set("variant", String(this.currentVariant.id));
    if (currentUrl.hash) nextUrl.hash = currentUrl.hash;
    window.history.replaceState({}, "", nextUrl.toString());

    if (!dispatch) return;
    document.dispatchEvent(
      new CustomEvent("productVariantUpdated", {
        detail: {
          name: this.currentVariant.name,
          sku: this.currentVariant.sku,
          variantId: this.currentVariant.id,
          variantImageId: this.currentVariant.featured_media?.id,
          variantImageSrc: this.currentVariant.featured_image?.src,
          variantImageAlt: this.currentVariant.featured_image?.alt,
          skipAccordionSectionFetch: true,
        },
      }),
    );
  }

  getFetchProductUrl() {
    for (const select of Array.from(this.cache.selects || [])) {
      const selected = select.options[select.selectedIndex];
      if (selected?.dataset?.productUrl) return selected.dataset.productUrl;
    }

    for (const btn of Array.from(this.cache.customButtons || [])) {
      if (btn.getAttribute("aria-current") === "true" && btn.dataset.productUrl) {
        return btn.dataset.productUrl;
      }
    }

    return this.dataset.url;
  }

  syncActiveProductUrl(productUrl) {
    for (const faq of document.querySelectorAll("[data-ek-product-faq]")) {
      faq.dataset.productUrl = productUrl;
    }
  }

  /** Insert metafield connected-product pills when the current DOM lacks them but the fetched section has them. */
  insertConnectedVariants(source, id) {
    const buybox = document.getElementById("buybox");
    if (!buybox) return;

    const variantSelects = buybox.querySelector("variant-selects");
    const node = source.cloneNode(true);
    const wrapper = document.createElement("div");
    wrapper.className = id === "connect-variants-mobile" ? "md:hidden" : "hidden md:block";
    wrapper.appendChild(node);

    if (variantSelects) {
      variantSelects.parentElement?.insertBefore(wrapper, variantSelects);
      return;
    }

    buybox.appendChild(wrapper);
  }

  /** Keep existing pills when a CL child section omits them; mark the link matching the new product URL active. */
  syncConnectedVariantsActiveState(wrapper, productUrl) {
    let nextPath = "";
    try {
      nextPath = new URL(productUrl, window.location.origin).pathname.replace(/\/$/, "");
    } catch {
      return;
    }
    if (!nextPath) return;

    const links = Array.from(wrapper.querySelectorAll("a[href]"));
    const matchesPill = links.some((link) => {
      try {
        return new URL(link.href, window.location.origin).pathname.replace(/\/$/, "") === nextPath;
      } catch {
        return false;
      }
    });
    // CL size changes go to child URLs that aren't in product_links — leave the wood pill as-is.
    if (!matchesPill) return;

    const activeClasses = ["!font-medium", "!bg-lightgray", "border-secondary", "bg-lightgray", "outline-none"];
    const inactiveClasses = ["border-separatorgray", "bg-white"];

    for (const link of links) {
      let linkPath = "";
      try {
        linkPath = new URL(link.href, window.location.origin).pathname.replace(/\/$/, "");
      } catch {
        continue;
      }
      const isActive = linkPath === nextPath;
      if (isActive) {
        link.setAttribute("data-activevariant", "");
        link.removeAttribute("data-nonactivevariant");
        link.classList.add(...activeClasses);
        link.classList.remove(...inactiveClasses);
      } else {
        link.setAttribute("data-nonactivevariant", "");
        link.removeAttribute("data-activevariant");
        link.classList.add(...inactiveClasses);
        link.classList.remove(...activeClasses);
      }
    }
  }

  updateVariantInput() {
    this.cache.productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (input) {
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }

  renderProductInfo(fetchProductUrl = this.getFetchProductUrl()) {
    const requestedVariantId = this.currentVariant.id;
    const fetchUrl = fetchProductUrl;
    const previousUrl = this.dataset.url;

    // Avoid redundant fetches
    if (this.state.lastVariantId === requestedVariantId && fetchUrl === previousUrl) {
      return Promise.resolve();
    }

    // Cancel any pending fetch
    if (this.state.pendingFetch) {
      this.state.pendingFetch.abort?.();
    }

    const sectionId = this.dataset.originalSection || this.dataset.section;
    const controller = new AbortController();
    this.state.pendingFetch = controller;

    // A/B template tests: keep rendering the test template, also for CL child URLs that have their own template
    const templateView = this.dataset.templateView ? `&view=${encodeURIComponent(this.dataset.templateView)}` : "";
    const fetchPromise = fetch(`${fetchUrl}?variant=${requestedVariantId}&section_id=${sectionId}${templateView}`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        // Check if this is still the current variant
        if (this.currentVariant.id !== requestedVariantId) return;

        this.state.lastVariantId = requestedVariantId;
        this.state.pendingFetch = null;

        if (fetchUrl !== previousUrl) {
          this.dataset.url = fetchUrl;
          this.syncActiveProductUrl(fetchUrl);
        }

        const html = new DOMParser().parseFromString(responseText, "text/html");
        const dispatched = this.updateProductElements(html, sectionId, fetchUrl !== previousUrl, fetchUrl);
        this.updateURL({ dispatch: !dispatched });
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          console.error("Failed to fetch product info:", error);
          const fallback = new URL(fetchUrl, window.location.origin);
          fallback.searchParams.set("variant", String(requestedVariantId));
          window.location.assign(fallback.toString());
        }
      });

    return fetchPromise;
  }

  /** @returns {boolean} True when productVariantUpdated was dispatched for the new variant. */
  updateProductElements(html, sectionId, productUrlChanged = false, fetchUrl = this.dataset.url) {
    let dispatched = false;
    // Only replace the full gallery when the variant selector switches to a different product
    // (e.g. combined listing: different option value → different linked product). Same product =
    // just update the main image.
    const fetchedGalleryImg = html.querySelector("img#variantImage");
    const fetchedGalleryWrapper = html.getElementById("pdpGalleryWrapper");
    const currentGalleryWrapper = document.getElementById("pdpGalleryWrapper");
    const currentProductId = currentGalleryWrapper?.getAttribute("data-gallery-product-id");
    const fetchedProductId = fetchedGalleryWrapper?.getAttribute("data-gallery-product-id");
    const galleryProductChanged = fetchedProductId != null && currentProductId !== fetchedProductId;
    if (productUrlChanged || galleryProductChanged) {
      const fetchedBreadcrumbs = html.getElementById("breadcrumbs");
      const currentBreadcrumbs = document.getElementById("breadcrumbs");
      if (fetchedBreadcrumbs && currentBreadcrumbs) {
        currentBreadcrumbs.innerHTML = fetchedBreadcrumbs.innerHTML;
      }
    }
    // replace the gallery
    if (fetchedGalleryWrapper && currentGalleryWrapper && galleryProductChanged) {
      // Tear down Carousel/Panzoom while their elements are still attached, then swap
      window.destroyPDPGallery?.();
      currentGalleryWrapper.innerHTML = fetchedGalleryWrapper.innerHTML;
      currentGalleryWrapper.setAttribute("data-gallery-product-id", fetchedProductId);
      if (typeof window.reinitPDPGallery === "function") {
        window.reinitPDPGallery();
      }
    }

    // Update price
    const destination = document.getElementById(`price-${this.dataset.section}`);
    const source = html.getElementById(`price-${this.dataset.originalSection || this.dataset.section}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
      destination.classList.remove("visibility-hidden");
    }

    const lowStock = html.getElementById(`LowStock-${sectionId}`);
    const targetLowStock = document.getElementById(`LowStock-${sectionId}`);
    if (lowStock && targetLowStock) {
      targetLowStock.innerHTML = lowStock.innerHTML;
    }

    // Update variant description
    const variantDescription = html.getElementById(`VariantDescription-${sectionId}`);
    const targetVariantDescription = document.getElementById(`VariantDescription-${sectionId}`);
    if (targetVariantDescription) {
      if (variantDescription) {
        // Update with new content (may be empty if variant has no description)
        targetVariantDescription.innerHTML = variantDescription.innerHTML;
        // Hide container if empty (no description for this variant)
        if (!variantDescription.innerHTML.trim()) {
          targetVariantDescription.style.display = "none";
        } else {
          targetVariantDescription.style.display = "";
        }
      } else {
        // Variant description element doesn't exist in fetched HTML, clear and hide it
        targetVariantDescription.innerHTML = "";
        targetVariantDescription.style.display = "none";
      }
    } else if (variantDescription && variantDescription.innerHTML.trim()) {
      // Target doesn't exist but source does - this shouldn't happen if snippet always renders container
      // But handle it gracefully by creating the element
      const buybox = document.getElementById("buybox");
      if (buybox) {
        const newElement = variantDescription.cloneNode(true);
        const priceElement = buybox.querySelector("#price-" + this.dataset.section);
        if (priceElement) {
          buybox.insertBefore(newElement, priceElement);
        } else {
          buybox.appendChild(newElement);
        }
      }
    }

    const featureList = html.getElementById(`PdpFeatureList-${sectionId}`);
    const targetFeatureList = document.getElementById(`PdpFeatureList-${sectionId}`);
    if (targetFeatureList && featureList) {
      targetFeatureList.innerHTML = featureList.innerHTML;
    }

    // Update product title
    const titles = html.querySelectorAll(".product-title");
    const targetTitles = document.querySelectorAll(".product-title");
    for (const [index, targetTitle] of targetTitles.entries()) {
      const title = titles[index];
      if (title) targetTitle.innerHTML = title.innerHTML;
    }

    // Update selected variant title
    const selectedVariantTitle = html.getElementById("product-selected-variant-title");
    const targetSelectedVariantTitle = document.getElementById("product-selected-variant-title");
    if (targetSelectedVariantTitle && selectedVariantTitle) {
      targetSelectedVariantTitle.innerHTML = selectedVariantTitle.innerHTML;
    }

    // Update mobile variant title (test version)
    const selectedVariantTitleMobile = html.getElementById("product-selected-variant-title-mobile");
    const targetSelectedVariantTitleMobile = document.getElementById("product-selected-variant-title-mobile");
    if (targetSelectedVariantTitleMobile && selectedVariantTitleMobile) {
      targetSelectedVariantTitleMobile.innerHTML = selectedVariantTitleMobile.innerHTML;
    }

    // Update waitlist form
    const waitlistForm = html.getElementById(`WaitlistForm-${sectionId}`);
    const targetWaitlistForm = document.getElementById(`WaitlistForm-${sectionId}`);
    if (targetWaitlistForm && waitlistForm) {
      targetWaitlistForm.innerHTML = waitlistForm.innerHTML;
    }

    // Update form
    const form = html.getElementById(`Form-${sectionId}`);
    const targetForm = document.getElementById(`Form-${sectionId}`);
    if (targetForm && form) {
      targetForm.innerHTML = form.innerHTML;
    }

    // Update delivery time: replace only the hidden data element (so data-variant-shipping stays in sync); update only the text span so the market selector keeps its options and listener
    const deliveryTime = html.getElementById(`DeliveryTime-${sectionId}`);
    const targetDeliveryTime = document.getElementById(`DeliveryTime-${sectionId}`);
    const deliveryTimeData = html.getElementById(`DeliveryTimeData-${sectionId}`);
    const targetDeliveryTimeData = document.getElementById(`DeliveryTimeData-${sectionId}`);
    if (deliveryTimeData && targetDeliveryTimeData) {
      targetDeliveryTimeData.replaceWith(deliveryTimeData.cloneNode(true));
    }
    if (deliveryTime && targetDeliveryTime) {
      const text = deliveryTime.querySelector(`#DeliveryTimeText-${sectionId}`);
      const targetText = targetDeliveryTime.querySelector(`#DeliveryTimeText-${sectionId}`);
      if (text && targetText) targetText.innerHTML = text.innerHTML;
    }
    const dataElement = document.getElementById(`DeliveryTimeData-${sectionId}`);
    if (dataElement) {
      dataElement.setAttribute("data-current-variant", this.currentVariant.id);
      const variantImageSrc = this.currentVariant.featured_image?.src;
      const variantImageAlt = this.currentVariant.featured_image?.alt;
      if (!variantImageSrc && fetchedGalleryImg) {
        const src = fetchedGalleryImg.getAttribute("src");
        if (src) {
          dispatched = true;
          document.dispatchEvent(
            new CustomEvent("productVariantUpdated", {
              detail: {
                name: this.currentVariant.name,
                sku: this.currentVariant.sku,
                variantId: this.currentVariant.id,
                variant: this.currentVariant,
                variantImageId: this.currentVariant.featured_media?.id,
                variantImageSrc: src,
                variantImageAlt: fetchedGalleryImg.getAttribute("alt") || "",
                sectionId: sectionId,
                source: "VariantSelects",
                accordionUpdated: true,
              },
            }),
          );
        }
      } else {
        dispatched = true;
        document.dispatchEvent(
          new CustomEvent("productVariantUpdated", {
            detail: {
              name: this.currentVariant.name,
              sku: this.currentVariant.sku,
              variantId: this.currentVariant.id,
              variant: this.currentVariant,
              variantImageId: this.currentVariant.featured_media?.id,
              variantImageSrc: variantImageSrc,
              variantImageAlt: variantImageAlt,
              sectionId: sectionId,
              source: "VariantSelects",
              accordionUpdated: true,
            },
          }),
        );
      }
    }

    // Update property delivery time
    const propertyDeliveryTime = html.getElementById(`PropertyDeliveryTime-${sectionId}`);
    const targetPropertyDeliveryTime = document.getElementById(`PropertyDeliveryTime-${sectionId}`);
    if (targetPropertyDeliveryTime && propertyDeliveryTime) {
      targetPropertyDeliveryTime.value = propertyDeliveryTime.value;
    }

    // Update benefits
    const benefits = html.getElementById("benefits-current");
    const targetBenefits = document.getElementById("benefits-current");
    if (benefits && targetBenefits) {
      targetBenefits.innerHTML = benefits.innerHTML;
    }

    // Update variant selectors: replace with re-rendered Liquid so option state (selected value, white images, availability) stays in sync
    const variantSelectorsSource = html.querySelector("#dynamic-variant-selects-container");
    const variantSelectorsTarget = document.querySelector("#dynamic-variant-selects-container");
    if (variantSelectorsSource && variantSelectorsTarget) {
      variantSelectorsTarget.innerHTML = variantSelectorsSource.innerHTML;
      if (typeof window.initVariantOptionLabelPaddingInContext === "function") {
        requestAnimationFrame(() => {
          window.initVariantOptionLabelPaddingInContext(variantSelectorsTarget);
        });
      }
    }

    // product_links pills live on the listing you entered (often the CL parent / a non-CL sibling).
    // CL size changes fetch a child section that usually has no #connect-variants — never strip the existing pills.
    for (const id of ["connect-variants", "connect-variants-mobile"]) {
      const source = html.getElementById(id);
      const target = document.getElementById(id);
      if (source && target) {
        target.replaceWith(source.cloneNode(true));
      } else if (source && !target) {
        this.insertConnectedVariants(source, id);
      } else if (!source && target && productUrlChanged) {
        this.syncConnectedVariantsActiveState(target, fetchUrl);
      }
    }

    if (window.accordionVariantUpdater?.applyAccordionFromDocument) {
      window.accordionVariantUpdater.applyAccordionFromDocument(html);
    }

    if (typeof window.setupButtons === "function") {
      window.setupButtons();
    }

    const currentUpsell = document.querySelector("ek-upsell-drawer");
    const fetchedUpsell = html.querySelector("ek-upsell-drawer");
    if (currentUpsell && fetchedUpsell) {
      currentUpsell.replaceWith(fetchedUpsell);
    }

    // Recommendations section lives in a different Shopify section than variant selectors, so it is
    // not in the fragment we fetched. Fetch it separately via Sections API and replace.
    const recSectionEl = document.querySelector(".ek-product-recommendations-section");
    if (recSectionEl) {
      const recSectionId = recSectionEl.dataset.sectionId;
      const variantId = this.currentVariant.id;
      if (recSectionId) {
        const recUrl = `${this.dataset.url}?variant=${variantId}&sections=${recSectionId}`;
        fetch(recUrl)
          .then((r) => r.json())
          .then((data) => {
            const sectionHtml = data[recSectionId];
            if (!sectionHtml || this.currentVariant?.id !== variantId) return;
            const wrapper = document.getElementById("shopify-section-" + recSectionId);
            if (wrapper) {
              // Sections API returns HTML wrapped in <div id="shopify-section-...">. Use only the
              // inner content to avoid nesting duplicate wrappers and duplicate IDs.
              const doc = new DOMParser().parseFromString(sectionHtml, "text/html");
              const root = doc.body.firstElementChild;
              const innerHtml = root && root.id === "shopify-section-" + recSectionId ? root.innerHTML : sectionHtml;
              wrapper.innerHTML = innerHtml;
              wrapper.dispatchEvent(new CustomEvent("shopify:section:load", { bubbles: true }));
            }
          })
          .catch((err) => {
            if (err.name !== "AbortError") console.error("Failed to fetch recommendations section:", err);
          });
      }
    }

    // Re-cache and re-bind variant buttons because some innerHTML was replaced
    // Force a fresh query - clear cache and re-query
    this.cache.customButtons = null;
    this.cache.selects = null;

    this.cacheElements();
    this.setupCustomVariantButtons();
    this.updateCustomVariantButtonUI();
    this.triggerValidCombinationFallback();
    // Respect availability after update
    if (!this.currentVariant?.available) {
      this.toggleAddButton(true, window.variantStrings?.soldOut || "Ausverkauft", true);
    } else {
      this.toggleAddButton(false);
    }

    return dispatched;
  }

  toggleAddButton(disable = true, text, isSoldOut = false) {
    const buttons = document.querySelectorAll(".button-addtocart");
    if (!buttons.length) return;

    buttons.forEach((button) => {
      const buttonText = button.querySelector("span");

      if (disable) {
        // Always disable button for sold-out or unavailable states
        button.setAttribute("disabled", "disabled");
        button.setAttribute("aria-disabled", "true");
        if (isSoldOut) {
          button.setAttribute("data-variant-sold-out", "true");
        } else {
          button.removeAttribute("data-variant-sold-out");
        }
        if (text && buttonText) buttonText.textContent = text;
      } else {
        button.removeAttribute("disabled");
        button.removeAttribute("data-variant-sold-out");
        button.removeAttribute("aria-disabled");
        if (buttonText && window.variantStrings?.addToCart) {
          buttonText.textContent = window.variantStrings.addToCart;
        }
      }
    });
  }

  setUnavailable() {
    const form = document.getElementById(`product-form-${this.dataset.section}-fields`);
    if (!form) return;

    const addButton = form.querySelector('[name="add"]');
    const addButtonText = form.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku = document.getElementById(`Sku-${this.dataset.section}`);

    if (addButton && addButtonText && window.variantStrings?.unavailable) {
      addButtonText.textContent = window.variantStrings.unavailable;
    }
    if (price) price.classList.add("visibility-hidden");
    if (inventory) inventory.classList.add("visibility-hidden");
    if (sku) sku.classList.add("visibility-hidden");
  }

  getVariantData() {
    if (this.variantData) return this.variantData;

    const scriptTag = this.querySelector('[type="application/json"]');
    if (!scriptTag) return [];

    try {
      this.variantData = JSON.parse(scriptTag.textContent);
      return this.variantData;
    } catch (error) {
      console.error("Failed to parse variant data:", error);
      return [];
    }
  }
}

customElements.define("variant-selects", VariantSelects);
